/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { Check, ListChecks } from "lucide-react";
import Page from "components/common_components/Page";
import Header from "components/common_components/Header";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import LoadingPage from "components/common_components/LoadingPage";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import { hasPermission } from "utils/session";
import FormsContext from "./FormsContext";
import PdfForm from "./components/PdfForm";
import CopyOverviewStep from "./components/CopyOverviewStep";
import CopyReviewList from "./components/CopyReviewList";
import CopyConfirmStep from "./components/CopyConfirmStep";
import { getFormattedValue, getFormPdfLocation, toAddendumsPayload } from "./utils";
import {
    COPY_TO_CURRENT_VERSION_PERMISSION,
    ReviewKind,
    copyAddendums,
    copyValuesToVersion,
    findCurrentVersion,
    loadPdfFormData,
} from "./utils/copyToVersion";

const Steps = [
    { id: "overview", label: "Overview" },
    { id: "review", label: "Review" },
    { id: "confirm", label: "Confirm" },
];

const Stepper = ({ current }) => (
    <ol className="cw-stepper">
        {Steps.map((step, index) => (
            <li key={step.id} className={clsx("cw-stepper__step", { active: index === current, done: index < current })}>
                <span className="cw-stepper__number">{index < current ? <Check size={14} /> : index + 1}</span>
                <span className="cw-stepper__label">{step.label}</span>
            </li>
        ))}
    </ol>
);

/**
 * Wizard that creates a new client form on the current version of a form, from a client form filled in an
 * old version. Values are copied to the fields with exactly the same PDF name. The user reviews, over the old
 * version, what is copied and what is not, and agrees before saving. The old client form is not changed.
 */
const CopyToVersionForm = () => {
    const { id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.Login);
    const { FormClients, Forms, Storage, getAttorneyUsersOptions } = useContext(FormsContext);
    const [step, setStep] = useState(0);
    const [sourceFormClient, setSourceFormClient] = useState(undefined);
    const [sourcePdfUrl, setSourcePdfUrl] = useState(null);
    const [targetForm, setTargetForm] = useState(undefined);
    const [copyResult, setCopyResult] = useState(null);
    const [addendums, setAddendums] = useState([]);
    const [attorneyUserId, setAttorneyUserId] = useState(null);
    const [fileCase, setFileCase] = useState(null);
    const [agreed, setAgreed] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // The old version is shown as it was filled in, the same way the form's view shows it
    const sourceValues = useMemo(
        () =>
            (sourceFormClient?.values || []).reduce(
                (obj, { value, formFieldMapping }) => ({
                    ...obj,
                    [formFieldMapping.pdfField]: getFormattedValue(formFieldMapping.type, value),
                }),
                {}
            ),
        [sourceFormClient]
    );

    const goToSource = () => history.push(`/forms/${id}`);

    const failAndGoBack = (error) => {
        dispatch(notifyError(error?.message || error || "Unexpected error."));
        goToSource();
    };

    useEffect(() => {
        if (!hasPermission(user, COPY_TO_CURRENT_VERSION_PERMISSION)) {
            failAndGoBack("You are not allowed to copy forms to the current version.");
            return;
        }
        FormClients.getOneById({
            id,
            onSuccess: (data) => {
                setSourceFormClient(data);
                setAttorneyUserId(data.attorneyUserId || null);
                setFileCase(data.fileCaseId ? { module: data.module, fileCaseId: data.fileCaseId, fileNo: data.fileNo } : null);
            },
            onError: (error) => {
                dispatch(notifyError(error.message || error));
                history.push("/forms");
            },
        });
    }, [id]);

    // The current versions are read again here: the list kept by the module may be stale or still loading.
    // That list has no field mappings, so the target version is then loaded with them.
    useEffect(() => {
        if (!sourceFormClient) return;
        Forms.getCurrent({
            onSuccess: (forms) => {
                const currentVersion = findCurrentVersion(forms, sourceFormClient.form);
                if (!currentVersion) {
                    failAndGoBack("There is no newer current version of this form.");
                    return;
                }
                Forms.getOneById({ id: currentVersion.id, onSuccess: setTargetForm, onError: failAndGoBack });
            },
            onError: failAndGoBack,
        });
    }, [sourceFormClient]);

    useEffect(() => {
        if (!targetForm) return undefined;

        let cancelled = false;
        (async () => {
            try {
                const [targetPdfUrl, oldPdfUrl] = await Promise.all([
                    Storage.getDownloadURLAsync(getFormPdfLocation(targetForm)),
                    Storage.getDownloadURLAsync(getFormPdfLocation(sourceFormClient.form)),
                ]);
                const [target, source] = await Promise.all([loadPdfFormData(targetPdfUrl), loadPdfFormData(oldPdfUrl)]);
                if (cancelled) return;
                setCopyResult(
                    copyValuesToVersion({
                        sourceValues: sourceFormClient.values,
                        targetMappings: targetForm.formFieldMappings,
                        target,
                        source,
                    })
                );
                setAddendums(copyAddendums(sourceFormClient.addendums, target.suggestions));
                setSourcePdfUrl(oldPdfUrl);
            } catch (error) {
                if (!cancelled) failAndGoBack(error);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [targetForm]);

    const save = () => {
        const mappingIdByField = new Map(targetForm.formFieldMappings.map((m) => [m.pdfField, m.id]));
        const payloadValues = Object.keys(copyResult.values)
            .filter((key) => mappingIdByField.has(key))
            .map((key) => ({
                formClientId: null,
                fieldMappingsId: mappingIdByField.get(key),
                value: copyResult.values[key],
            }));

        setIsSubmitting(true);
        FormClients.add({
            payload: {
                clientId: sourceFormClient.clientId,
                formId: targetForm.id,
                status: "open",
                attorneyUserId,
                module: fileCase?.module || null,
                fileCaseId: fileCase?.fileCaseId || null,
                fileNo: fileCase?.fileNo || null,
                values: payloadValues,
                addendums: toAddendumsPayload(addendums),
            },
            onSuccess: (newId) => {
                dispatch(notifySuccess(`Form copied to version ${targetForm.version} successfully`));
                setIsSubmitting(false);
                history.push(`/forms/${newId}`);
            },
            onError: (error) => {
                dispatch(notifyError(error.message || error));
                setIsSubmitting(false);
            },
        });
    };

    const isReady = sourceFormClient && targetForm && copyResult;
    const attorneyOptions = getAttorneyUsersOptions();
    const reviewItems = copyResult?.reviewItems || [];
    const tooLongCount = reviewItems.filter((item) => item.kind === ReviewKind.TooLong).length;
    const notCopiedCount = reviewItems.length - tooLongCount;
    const canSave = reviewItems.length === 0 || agreed;

    return (
        <Page className="copy-form-page">
            <Header title="Copy form to current version" />
            {!isReady ? (
                <LoadingPage />
            ) : (
                <>
                    <div className="form-head cw-bar">
                        <Stepper current={step} />
                        <div className="item cw-bar__form">
                            {sourceFormClient.form?.code}
                            <span>
                                ({sourceFormClient.form?.version} → {targetForm.version})
                            </span>
                        </div>
                        <div className="item cw-bar__client">{sourceFormClient.client?.fullName}</div>
                        <div className="item form-actions">
                            <Button secondary outline small className="cancel" onClick={goToSource}>
                                Cancel
                            </Button>
                            {step > 0 && (
                                <Button secondary outline small onClick={() => setStep(step - 1)}>
                                    <span>Back</span>
                                </Button>
                            )}
                            {step < Steps.length - 1 ? (
                                <Button secondary small onClick={() => setStep(step + 1)}>
                                    <span>Continue</span>
                                </Button>
                            ) : (
                                <Button
                                    secondary
                                    small
                                    loading={isSubmitting}
                                    disabled={!canSave}
                                    onClick={save}
                                    title={canSave ? undefined : "Agree with the values that are not copied as they are first"}
                                >
                                    <Icon name="save" size="sm" color="white" />
                                    <span>Save</span>
                                </Button>
                            )}
                        </div>
                    </div>

                    {step === 0 && (
                        <CopyOverviewStep
                            sourceFormClient={sourceFormClient}
                            targetForm={targetForm}
                            copyResult={copyResult}
                            addendumsCount={addendums.length}
                            attorneyOptions={attorneyOptions}
                            attorneyUserId={attorneyUserId}
                            onAttorneyChange={setAttorneyUserId}
                            fileCase={fileCase}
                            onFileCaseChange={(selected) =>
                                setFileCase(
                                    selected
                                        ? { module: selected.module, fileCaseId: selected.fileCaseId, fileNo: selected.fileNo }
                                        : null
                                )
                            }
                        />
                    )}

                    {step === 1 && (
                        <PdfForm
                            pdfUrl={sourcePdfUrl}
                            mode="view"
                            formValues={sourceValues}
                            fieldMappings={sourceFormClient.form?.formFieldMappings}
                            addendumInfo={{ formCode: sourceFormClient.form?.code }}
                            addendums={sourceFormClient.addendums || []}
                            downloadFileNameGetter={() =>
                                `${sourceFormClient.form?.code}_${sourceFormClient.form?.version}_${sourceFormClient.client?.fullName}.pdf`
                            }
                            fieldHighlights={copyResult.highlights}
                            sidePanel={{
                                label: "Review",
                                count: reviewItems.length,
                                icon: <ListChecks size={16} />,
                                content: <CopyReviewList items={reviewItems} />,
                            }}
                        />
                    )}

                    {step === 2 && (
                        <CopyConfirmStep
                            sourceFormClient={sourceFormClient}
                            targetForm={targetForm}
                            attorneyName={attorneyOptions.find((x) => x.id === attorneyUserId)?.name}
                            fileNo={fileCase?.fileNo}
                            copiedCount={copyResult.copiedCount}
                            notCopiedCount={notCopiedCount}
                            tooLongCount={tooLongCount}
                            addendumsCount={addendums.length}
                            agreed={agreed}
                            onAgreedChange={setAgreed}
                        />
                    )}
                </>
            )}
        </Page>
    );
};

export default CopyToVersionForm;
